import React, { BlockquoteHTMLAttributes, ComponentProps, JSX } from 'react';
import type { Components } from 'react-markdown';

// Define props type for markdown components
type MarkdownComponentProps<
  T extends keyof JSX.IntrinsicElements
> = ComponentProps<T> & {
  children?: React.ReactNode;
};

// Define specific blockquote props
interface BlockquoteProps extends BlockquoteHTMLAttributes<HTMLQuoteElement> {
  children?: React.ReactNode;
  hideQuotes?: boolean;
}

const MarkdownComponents: Partial<Components> = {
  h1: ({ children, ...props }: MarkdownComponentProps<'h1'>) => (
    <h1 style={{ fontSize: '1.5em' }} {...props}>
      {children}
    </h1>
  ),

  h2: ({ children, ...props }: MarkdownComponentProps<'h2'>) => (
    <h2 style={{ fontSize: '1.3em' }} {...props}>
      {children}
    </h2>
  ),

  h3: ({ children, ...props }: MarkdownComponentProps<'h3'>) => (
    <h3 style={{ fontSize: '1.1em' }} {...props}>
      {children}
    </h3>
  ),

  h4: ({ children, ...props }: MarkdownComponentProps<'h4'>) => (
    <h4 style={{ fontSize: '1em' }} {...props}>
      {children}
    </h4>
  ),

  h5: ({ children, ...props }: MarkdownComponentProps<'h5'>) => (
    <h5 style={{ fontSize: '0.9em' }} {...props}>
      {children}
    </h5>
  ),

  h6: ({ children, ...props }: MarkdownComponentProps<'h6'>) => (
    <h6 style={{ fontSize: '0.8em' }} {...props}>
      {children}
    </h6>
  ),

  p: ({ children, ...props }: MarkdownComponentProps<'p'>) => (
    <p {...props}>{children}</p>
  ),

  strong: ({ children, ...props }: MarkdownComponentProps<'strong'>) => (
    <strong
      style={{
        fontWeight: 600,
        display: 'inline',
      }}
      {...props}
    >
      {children}
    </strong>
  ),

  ul: ({ children, ...props }: MarkdownComponentProps<'ul'>) => (
    <ul
      style={{
        paddingLeft: '1.5em',
        marginTop: '0.5em',
        marginBottom: '0.5em',
        listStyleType: 'disc',
      }}
      {...props}
    >
      {children}
    </ul>
  ),

  ol: ({ children, ...props }: MarkdownComponentProps<'ol'>) => (
    <ol
      style={{
        paddingLeft: '1.5em',
        marginTop: '0.5em',
        marginBottom: '0.5em',
      }}
      {...props}
    >
      {children}
    </ol>
  ),

  li: ({ children, ...props }: MarkdownComponentProps<'li'>) => (
    <li
      style={{
        marginBottom: '0.5em',
        marginTop: '0.5em',
        lineHeight: '1.6',
      }}
      {...props}
    >
      {children}
    </li>
  ),

  table: ({ children, ...props }: MarkdownComponentProps<'table'>) => (
    <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props}>
      {children}
    </table>
  ),

  td: ({ children, ...props }: MarkdownComponentProps<'td'>) => (
    <td
      style={{
        border: '1px solid rgba(224, 224, 224, 1)',
        padding: '8px',
      }}
      {...props}
    >
      {children}
    </td>
  ),

  th: ({ children, ...props }: MarkdownComponentProps<'th'>) => (
    <th
      style={{
        border: '1px solid rgba(224, 224, 224, 1)',
        padding: '8px',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      }}
      {...props}
    >
      {children}
    </th>
  ),

  blockquote: ({ children, hideQuotes, ...props }: BlockquoteProps) => (
    <blockquote
      style={{
        borderLeft: '4px solid rgba(0, 0, 0, 0.12)',
        marginLeft: 0,
        paddingLeft: '16px',
        fontStyle: 'italic',
        display: hideQuotes ? 'none' : 'block',
      }}
      {...props}
    >
      {children}
    </blockquote>
  ),

  code: ({ children, ...props }: MarkdownComponentProps<'code'>) => (
    <code
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        padding: '2px 4px',
        borderRadius: '4px',
        fontSize: '0.9em',
      }}
      {...props}
    >
      {children}
    </code>
  ),
};

export default MarkdownComponents;
